import * as React from 'react';
import { CellPlugin } from '@react-page/editor';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SiteMaintenanceBanner, {
    BANNER_BACKGROUND_COLOR,
    BANNER_FONT_SIZE,
    BANNER_FONT_WEIGHT,
    BANNER_MESSAGE,
    BANNER_PADDING,
    BANNER_TEXT_ALIGN,
    BANNER_TEXT_COLOR,
    DISABLE_BUTTONS,
    DISABLE_NAV_LINKS,
    DISABLED_TOOLTIP,
    END_DATE_TIME,
    IS_ENABLED,
    SELECTED_TIMEZONE,
    START_DATE_TIME,
    TIMEZONE_OPTIONS,
    coerceBool,
} from './components/SiteMaintenanceBanner';

const STYLE_ID = "site-maintenance-banner-plugin-fix";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
        [style*="max-height: 50vh"][style*="width: 100vw"] > div[style*="flex-direction: row"] {
            width: 100%;
            flex: 1 1 auto;
        }
    `;
    document.head.appendChild(style);
}

type Data = {
    isEnabled: boolean;
    bannerMessage: string;
    bannerBackgroundColor: string;
    bannerTextColor: string;
    bannerFontSize: string;
    bannerFontWeight: string;
    bannerTextAlign: string;
    bannerPadding: string;
    startDateTime: string;
    endDateTime: string;
    selectedTimezone: string;
    disableButtons: boolean;
    disableNavLinks: boolean;
    disabledTooltip: string;
};

const resolve = (data: Partial<Data>): Data => ({
    isEnabled: coerceBool(data.isEnabled, IS_ENABLED),
    bannerMessage: data.bannerMessage ?? BANNER_MESSAGE,
    bannerBackgroundColor: data.bannerBackgroundColor ?? BANNER_BACKGROUND_COLOR,
    bannerTextColor: data.bannerTextColor ?? BANNER_TEXT_COLOR,
    bannerFontSize: data.bannerFontSize ?? BANNER_FONT_SIZE,
    bannerFontWeight: data.bannerFontWeight ?? BANNER_FONT_WEIGHT,
    bannerTextAlign: data.bannerTextAlign ?? BANNER_TEXT_ALIGN,
    bannerPadding: data.bannerPadding ?? BANNER_PADDING,
    startDateTime: data.startDateTime ?? START_DATE_TIME,
    endDateTime: data.endDateTime ?? END_DATE_TIME,
    selectedTimezone: data.selectedTimezone ?? SELECTED_TIMEZONE,
    disableButtons: coerceBool(data.disableButtons, DISABLE_BUTTONS),
    disableNavLinks: coerceBool(data.disableNavLinks, DISABLE_NAV_LINKS),
    disabledTooltip: data.disabledTooltip ?? DISABLED_TOOLTIP,
});

const s = {
    panel: {
        display: "flex" as const,
        flexDirection: "column" as const,
        gap: "16px",
        padding: "20px",
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box" as const,
    },
    label: {
        fontWeight: 500,
        fontSize: "13px",
        color: "#333",
        marginBottom: "5px",
        display: "block",
    } as React.CSSProperties,
    hint: {
        fontSize: "12px",
        color: "#6c757d",
        margin: "4px 0 0",
    } as React.CSSProperties,
    input: {
        display: "block",
        width: "100%",
        boxSizing: "border-box" as const,
        padding: "6px 10px",
        fontSize: "13px",
        border: "1px solid #ced4da",
        borderRadius: "4px",
        backgroundColor: "#fff",
        color: "#333",
        minWidth: 0,
    },
    select: {
        display: "block",
        width: "100%",
        boxSizing: "border-box" as const,
        padding: "6px 10px",
        fontSize: "13px",
        border: "1px solid #ced4da",
        borderRadius: "4px",
        backgroundColor: "#fff",
        color: "#333",
        cursor: "pointer",
        minWidth: 0,
    },
    divider: {
        border: "none",
        borderTop: "1px solid #e9ecef",
        margin: 0,
        width: "100%",
    } as React.CSSProperties,
    row: {
        display: "flex" as const,
        alignItems: "center" as const,
        justifyContent: "space-between" as const,
        width: "100%",
        minWidth: 0,
        gap: "12px",
    },
    field: {
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box" as const,
    },
};

const Toggle = ({ label, checked, onChange }: {
    label: string;
    checked: boolean;
    onChange: (v: boolean) => void;
}) => (
    <div style={s.row}>
        <span style={s.label}>{label}</span>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            <div
                onClick={() => onChange(!checked)}
                style={{
                    width: "40px",
                    height: "22px",
                    borderRadius: "11px",
                    backgroundColor: checked ? "#0057b6" : "#ced4da",
                    position: "relative",
                    cursor: "pointer",
                    flexShrink: 0,
                }}
            >
                <div style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    position: "absolute",
                    top: "3px",
                    left: checked ? "21px" : "3px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                }} />
            </div>
            <span style={{ fontSize: "13px", color: "#6c757d", width: "24px" }}>
                {checked ? "On" : "Off"}
            </span>
        </div>
    </div>
);

const Field = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
    <div style={s.field}>
        <label style={s.label}>{label}</label>
        {children}
        {hint && <p style={s.hint}>{hint}</p>}
    </div>
);

const SettingsControls = ({ data, onChange }: { data: Data; onChange: (d: Partial<Data>) => void }) => (
    <div style={s.panel}>
        <Toggle label="Enable maintenance mode" checked={data.isEnabled} onChange={(v) => onChange({ isEnabled: v })} />
        <hr style={s.divider} />
        <Field label="Banner message">
            <textarea rows={3} value={data.bannerMessage} onChange={(e) => onChange({ bannerMessage: e.target.value })} style={{ ...s.input, resize: "vertical" }} />
        </Field>
        <hr style={s.divider} />
        <Toggle label="Disable all buttons" checked={data.disableButtons} onChange={(v) => onChange({ disableButtons: v })} />
        <Toggle label="Disable navigation links" checked={data.disableNavLinks} onChange={(v) => onChange({ disableNavLinks: v })} />
    </div>
);

const ScheduleControls = ({ data, onChange }: { data: Data; onChange: (d: Partial<Data>) => void }) => {
    const tz = data.selectedTimezone;
    const tzLabel = TIMEZONE_OPTIONS.find(t => t.value === tz)?.label ?? tz;
    const endBeforeStart = !!data.startDateTime && !!data.endDateTime && data.endDateTime <= data.startDateTime;

    return (
        <div style={s.panel}>
            <Field label="Timezone" hint="All times below are interpreted in this timezone.">
                <select value={tz} onChange={(e) => onChange({ selectedTimezone: e.target.value })} style={s.select}>
                    {TIMEZONE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </Field>
            <Field label={`Start date & time (${tzLabel})`}>
                <input type="datetime-local" value={data.startDateTime} onChange={(e) => onChange({ startDateTime: e.target.value })} style={s.input} />
            </Field>
            <Field label={`End date & time (${tzLabel})`}>
                <input type="datetime-local" value={data.endDateTime} min={data.startDateTime || undefined} onChange={(e) => onChange({ endDateTime: e.target.value })} style={s.input} />
                {endBeforeStart && <p style={{ ...s.hint, color: "#dc3545" }}>End time must be after start time.</p>}
            </Field>
            <p style={s.hint}>
                When both dates are set, maintenance mode activates automatically within the window — the manual toggle is ignored.
                Leave both blank to use the manual toggle only.
            </p>
        </div>
    );
};

const ColorField = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
    <Field label={label}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <input type="color" value={value} onChange={(e) => onChange(e.target.value)} style={{ width: "48px", height: "34px", padding: "2px", border: "1px solid #ced4da", borderRadius: "4px", cursor: "pointer", flexShrink: 0 }} />
            <span style={{ fontSize: "13px", color: "#6c757d", fontFamily: "monospace" }}>{value}</span>
        </div>
    </Field>
);

const StylesControls = ({ data, onChange }: { data: Data; onChange: (d: Partial<Data>) => void }) => (
    <div style={s.panel}>
        <ColorField label="Background color" value={data.bannerBackgroundColor} onChange={(v) => onChange({ bannerBackgroundColor: v })} />
        <ColorField label="Text color" value={data.bannerTextColor} onChange={(v) => onChange({ bannerTextColor: v })} />
        <Field label="Font size">
            <input type="text" value={data.bannerFontSize} onChange={(e) => onChange({ bannerFontSize: e.target.value })} placeholder="e.g. 15px, 1rem" style={s.input} />
        </Field>
        <Field label="Font weight">
            <select value={data.bannerFontWeight} onChange={(e) => onChange({ bannerFontWeight: e.target.value })} style={s.select}>
                <option value="400">Normal (400)</option>
                <option value="500">Medium (500)</option>
                <option value="600">Semi-bold (600)</option>
                <option value="700">Bold (700)</option>
            </select>
        </Field>
        <Field label="Text alignment">
            <select value={data.bannerTextAlign} onChange={(e) => onChange({ bannerTextAlign: e.target.value })} style={s.select}>
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
            </select>
        </Field>
        <Field label="Padding" hint="CSS shorthand — vertical horizontal (e.g. 12px 24px)">
            <input type="text" value={data.bannerPadding} onChange={(e) => onChange({ bannerPadding: e.target.value })} placeholder="e.g. 12px 24px" style={s.input} />
        </Field>
        <hr style={s.divider} />
        <Field label="Preview">
            <div style={{
                backgroundColor: data.bannerBackgroundColor,
                color: data.bannerTextColor,
                padding: data.bannerPadding,
                fontSize: data.bannerFontSize,
                fontWeight: data.bannerFontWeight as any,
                borderRadius: "4px",
                border: `1px solid ${data.bannerTextColor}33`,
                lineHeight: 1.5,
                width: "100%",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: data.bannerTextAlign === "center" ? "center" : data.bannerTextAlign === "right" ? "flex-end" : "flex-start",
            }}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: "#FFC107", fontSize: "18px", marginRight: "10px", flexShrink: 0 }} />
                {data.bannerMessage || BANNER_MESSAGE}
            </div>
        </Field>
    </div>
);

const SiteMaintenanceBannerPlugin: CellPlugin<Data> = {
    Renderer: ({ data }) => (
        <SiteMaintenanceBanner {...resolve(data)} />
    ),
    id: 'siteMaintenanceBannerPlugin',
    title: 'Site Maintenance Banner',
    description: 'Configures a maintenance banner shown on the live site. Schedule, toggle, and disable options.',
    version: 1,
    controls: [
        {
            title: 'Settings',
            controls: {
                type: 'custom',
                Component: ({ data, onChange }) => (
                    <SettingsControls data={resolve(data)} onChange={onChange} />
                ),
            },
        },
        {
            title: 'Schedule',
            controls: {
                type: 'custom',
                Component: ({ data, onChange }) => (
                    <ScheduleControls data={resolve(data)} onChange={onChange} />
                ),
            },
        },
        {
            title: 'Styles',
            controls: {
                type: 'custom',
                Component: ({ data, onChange }) => (
                    <StylesControls data={resolve(data)} onChange={onChange} />
                ),
            },
        },
    ],
};

export default SiteMaintenanceBannerPlugin;